import { Link } from "react-router-dom";

function TocItem({ id, label }) {
  return (
    <li>
      <a href={`#${id}`} className="text-[#5DADE2] font-semibold hover:underline">{label}</a>
    </li>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 mt-10">
      <h2 className="text-xl sm:text-2xl font-extrabold text-[#243B53]">{title}</h2>
      <div className="mt-3 space-y-3 text-[#243B53]/85 leading-relaxed text-[15px]">
        {children}
      </div>
    </section>
  );
}

export default function Terms() {
  return (
    <div className="max-w-3xl mx-auto p-6 sm:p-10" data-testid="terms-page">
      <Link to="/" data-testid="terms-back-link" className="text-[#5DADE2] font-bold text-sm">← Voltar ao Clubinho</Link>
      <h1 className="text-3xl sm:text-4xl font-extrabold text-[#243B53] mt-4">Termos de Uso e Assinatura</h1>
      <p className="text-sm text-[#243B53]/60 mt-2">Última atualização: setembro de 2026</p>

      <div className="card-soft p-5 mt-6 bg-[#FFF9ED]">
        <p className="text-sm text-[#243B53]/85">
          Estes Termos regulam o uso da plataforma <b>Clubinho da Fé</b>, produto de
          educação cristã infantil, e a contratação da assinatura. Leia em conjunto com
          a nossa <Link to="/privacy" className="text-[#5DADE2] font-bold">Política de Privacidade</Link>.
        </p>
      </div>

      <nav className="card-soft p-5 mt-6" aria-label="Sumário">
        <p className="text-sm font-extrabold text-[#243B53] uppercase tracking-wide">Sumário</p>
        <ul className="mt-3 space-y-1 text-sm columns-1 sm:columns-2">
          <TocItem id="identificacao" label="1. Identificação" />
          <TocItem id="objeto" label="2. Objeto" />
          <TocItem id="publico" label="3. Público e responsabilidade" />
          <TocItem id="conta" label="4. Conta e credenciais" />
          <TocItem id="assinatura" label="5. Assinatura" />
          <TocItem id="renovacao" label="6. Renovação recorrente" />
          <TocItem id="pagamento" label="7. Pagamento" />
          <TocItem id="acesso" label="8. Liberação de acesso" />
          <TocItem id="cancelamento" label="9. Cancelamento" />
          <TocItem id="arrependimento" label="10. Arrependimento e reembolso" />
          <TocItem id="inadimplencia" label="11. Inadimplência e chargeback" />
          <TocItem id="propriedade" label="12. Conteúdo e propriedade intelectual" />
          <TocItem id="religioso" label="13. Conteúdo religioso e educacional" />
          <TocItem id="disponibilidade" label="14. Disponibilidade" />
          <TocItem id="condutas" label="15. Condutas proibidas" />
          <TocItem id="suspensao" label="16. Suspensão" />
          <TocItem id="privacidade" label="17. Privacidade" />
          <TocItem id="criancas" label="18. Crianças e adolescentes" />
          <TocItem id="alteracoes" label="19. Alterações destes Termos" />
          <TocItem id="legislacao" label="20. Legislação" />
          <TocItem id="contato" label="21. Contato" />
        </ul>
      </nav>

      <Section id="identificacao" title="1. Identificação">
        <p>
          O Clubinho da Fé é operado por <b>CREATIVE SHOOP</b>, inscrita no CNPJ nº
          <b> 65.465.060/0001-25</b>, disponibilizado em
          {" "}<a href="https://clubinhodafeof.com" className="text-[#5DADE2] font-bold" target="_blank" rel="noopener noreferrer">clubinhodafeof.com</a>.
        </p>
      </Section>

      <Section id="objeto" title="2. Objeto">
        <p>
          Estes Termos disciplinam o acesso digital, mediante assinatura, à plataforma de
          educação cristã infantil Clubinho da Fé, que disponibiliza histórias, atividades,
          jornadas, devocionais, materiais de apoio e recursos de gamificação para o
          contexto familiar e educacional.
        </p>
      </Section>

      <Section id="publico" title="3. Público e responsabilidade do adulto">
        <p>
          A contratação da assinatura deve ser realizada por pessoa maior de idade e
          juridicamente capaz, na condição de responsável legal pelo(s) perfil(is)
          infantil(is) criados na plataforma. O uso por crianças ocorre sempre sob
          supervisão apropriada do responsável, observado o melhor interesse da criança
          e do adolescente.
        </p>
      </Section>

      <Section id="conta" title="4. Conta e credenciais">
        <ul className="list-disc ml-6 space-y-1">
          <li>A conta é pessoal e intransferível;</li>
          <li>O usuário é responsável por manter suas credenciais em sigilo;</li>
          <li>O usuário é responsável pela veracidade das informações fornecidas;</li>
          <li>É proibido o compartilhamento abusivo, coletivo ou fraudulento das credenciais.</li>
        </ul>
      </Section>

      <Section id="assinatura" title="5. Assinatura">
        <p>
          O plano atualmente oferecido é de <b>R$ 29,90 por mês</b>. O preço e as
          condições comerciais efetivamente apresentados no checkout no momento da
          contratação prevalecem para aquela contratação específica, quando aplicável.
        </p>
      </Section>

      <Section id="renovacao" title="6. Renovação recorrente">
        <p>
          A assinatura é recorrente mensal e renovada automaticamente enquanto permanecer
          ativa, conforme as condições e periodicidade apresentadas no checkout e mantidas
          pelo processador de pagamento.
        </p>
      </Section>

      <Section id="pagamento" title="7. Pagamento">
        <p>
          O pagamento é processado pela plataforma <b>Eduzz</b>, responsável pela
          intermediação da cobrança, meios de pagamento aceitos, tokenização e demais
          condições operacionais. A CREATIVE SHOOP não armazena dados completos de cartão
          de crédito do assinante.
        </p>
      </Section>

      <Section id="acesso" title="8. Liberação de acesso">
        <p>
          O acesso à área interna do Clubinho da Fé está condicionado à existência de
          assinatura válida e corretamente reconhecida pelos sistemas de pagamento e
          provisionamento. <b>Não há plano gratuito.</b> Após a confirmação da assinatura,
          o assinante recebe um e-mail para ativar sua conta e definir sua senha; o acesso
          é mantido enquanto o direito de acesso permanecer válido.
        </p>
      </Section>

      <Section id="cancelamento" title="9. Cancelamento">
        <p>
          O assinante pode solicitar o cancelamento pelos meios disponibilizados pelo
          processador da assinatura e/ou pelos canais oficiais aplicáveis. O cancelamento
          impede novas renovações conforme as regras contratuais e as regras do processador
          de pagamento. O cancelamento, por si só, não gera devolução automática de
          mensalidades anteriores já usufruídas.
        </p>
      </Section>

      <Section id="arrependimento" title="10. Direito de arrependimento e reembolso">
        <p>
          O Clubinho da Fé respeita integralmente os direitos obrigatórios previstos na
          legislação brasileira de defesa do consumidor, incluindo o direito de
          arrependimento quando legalmente aplicável. Solicitações de reembolso serão
          analisadas conforme a legislação vigente e as regras do meio de pagamento
          utilizado, sem prejuízo dos direitos legais do consumidor. Nenhuma cláusula
          destes Termos afasta direitos que a lei conferir ao consumidor.
        </p>
      </Section>

      <Section id="inadimplencia" title="11. Inadimplência, chargeback e reembolso">
        <p>
          O acesso poderá ser suspenso ou revogado quando a assinatura deixar de conferir
          direito de acesso, conforme situação confirmada pelos sistemas de pagamento e
          pelas regras da plataforma, incluindo inadimplência, estorno, contestação
          (chargeback), cancelamento ou reembolso.
        </p>
      </Section>

      <Section id="propriedade" title="12. Conteúdo e propriedade intelectual">
        <p>
          O conteúdo disponibilizado (textos, ilustrações, atividades, materiais para
          imprimir, séries, jornadas, marcas e demais elementos) é protegido pela
          legislação de propriedade intelectual e destinado ao <b>uso pessoal, familiar e
          educacional</b> do assinante, dentro das condições do produto.
        </p>
        <p><b>É proibido:</b></p>
        <ul className="list-disc ml-6 space-y-1">
          <li>Revenda a qualquer título;</li>
          <li>Distribuição pública não autorizada;</li>
          <li>Reprodução comercial;</li>
          <li>Disponibilização massiva de PDFs ou de qualquer conteúdo do Clubinho;</li>
          <li>Cópia, imitação, engenharia reversa ou tentativa de reproduzir a plataforma;</li>
          <li>Remoção de marcas d’água, créditos ou avisos de titularidade.</li>
        </ul>
        <p>
          Impressão e download só são permitidos quando a própria funcionalidade ou o
          próprio conteúdo assim autorizar expressamente.
        </p>
      </Section>

      <Section id="religioso" title="13. Conteúdo religioso e educacional">
        <p>
          O Clubinho da Fé tem proposta de <b>educação cristã infantil</b>. Os materiais
          possuem finalidade educacional e de apoio familiar e religioso, não substituindo
          orientação pastoral, pedagógica, psicológica, jurídica ou médica.
        </p>
      </Section>

      <Section id="disponibilidade" title="14. Disponibilidade">
        <p>
          A plataforma pode apresentar manutenções programadas ou indisponibilidades
          técnicas eventuais. Não prometemos disponibilidade de 100%. Envidamos esforços
          razoáveis para manter o serviço acessível, íntegro e seguro.
        </p>
      </Section>

      <Section id="condutas" title="15. Condutas proibidas">
        <ul className="list-disc ml-6 space-y-1">
          <li>Fraude, falsidade de informações ou uso indevido de meios de pagamento;</li>
          <li>Tentativa de invasão, exploração de vulnerabilidades ou acesso indevido;</li>
          <li>Abuso técnico ou operacional da plataforma;</li>
          <li>Compartilhamento ilícito, distribuição ou revenda de conteúdo;</li>
          <li>Exploração comercial não autorizada;</li>
          <li>Violação de direitos de terceiros, incluindo propriedade intelectual e proteção de crianças.</li>
        </ul>
      </Section>

      <Section id="suspensao" title="16. Suspensão">
        <p>
          A CREATIVE SHOOP poderá suspender ou revogar o acesso em caso de violação destes
          Termos, fraude, risco de segurança, ordem legal ou ausência de direito de acesso,
          observada a legislação aplicável.
        </p>
      </Section>

      <Section id="privacidade" title="17. Privacidade">
        <p>
          O tratamento de dados pessoais é regido pela nossa
          {" "}<Link to="/privacy" className="text-[#5DADE2] font-bold">Política de Privacidade</Link>,
          parte integrante destes Termos.
        </p>
      </Section>

      <Section id="criancas" title="18. Crianças e adolescentes">
        <p>
          O uso da plataforma pelas crianças deve observar a supervisão do responsável e
          a proteção do melhor interesse da criança e do adolescente, conforme a
          legislação vigente, incluindo a Lei nº 13.709/2018 (LGPD), o Estatuto da Criança
          e do Adolescente e as obrigações aplicáveis do Estatuto Digital da Criança e do
          Adolescente (Lei nº 15.211/2025 e regulamentação vigente).
        </p>
      </Section>

      <Section id="alteracoes" title="19. Alterações destes Termos">
        <p>
          Estes Termos podem ser atualizados. Mudanças relevantes serão publicadas nesta
          página com indicação da data da última atualização, respeitados os direitos
          adquiridos e a legislação aplicável.
        </p>
      </Section>

      <Section id="legislacao" title="20. Legislação aplicável">
        <p>
          Estes Termos são regidos pela legislação brasileira. Ficam preservados os
          direitos legais do consumidor, inclusive quanto ao foro competente segundo as
          normas de defesa do consumidor aplicáveis.
        </p>
      </Section>

      <Section id="contato" title="21. Contato">
        <p>
          Consulte os canais de atendimento disponibilizados no site do Clubinho da Fé em
          {" "}<a href="https://clubinhodafeof.com" className="text-[#5DADE2] font-bold" target="_blank" rel="noopener noreferrer">clubinhodafeof.com</a>.
        </p>
        <p className="text-sm text-[#243B53]/70">
          Operadora: CREATIVE SHOOP — CNPJ 65.465.060/0001-25.
        </p>
      </Section>

      <div className="mt-12 flex flex-wrap gap-3 text-sm">
        <Link to="/privacy" data-testid="terms-to-privacy" className="btn-outline">Ver Política de Privacidade</Link>
        <Link to="/" data-testid="terms-to-home" className="btn-outline">Voltar ao início</Link>
      </div>
    </div>
  );
}
